import { useMemo } from "react";
import { SectionShell } from "@/pages/SectionShell";
import { useI18n, useT } from "@/contexts/I18nContext";
import { useCallable } from "@/lib/hooks/useCallable";
import { CALLABLES } from "@/services/callables";
import { formatDateTime, formatNumber } from "@/lib/format";
import {
  JOB_STATE_KEY,
  JOB_STATE_TONE,
  jobsNeedingAttention,
  series,
  sortJobs,
  todayIsSpiking,
  type Metric,
  type PlatformHealth,
} from "./health";

/** The metrics with a series worth reading. */
const METRICS: Metric[] = ["callRefused", "emailSent", "emailFailed", "webhookFailed"];

/**
 * Health.
 *
 * Only what this system actually observes about itself. Firestore rule denials
 * are a Cloud Monitoring metric and no API hands them to a Cloud Function, so
 * what is here instead is the count of refused CALLS — labelled as that, because
 * a page saying "rule denials" while showing something else would be worse than
 * a page without them.
 */
export function HealthPage() {
  const t = useT();
  const { locale } = useI18n();

  const health = useCallable<PlatformHealth>(CALLABLES.getPlatformHealth);
  const jobs = useMemo(() => sortJobs(health.data?.jobs ?? []), [health.data]);
  const attention = jobsNeedingAttention(jobs);

  return (
    <SectionShell titleKey="health.title">
      {health.loading ? (
        <p className="text-sm text-ink-muted">{t("common.loading")}</p>
      ) : health.failed || !health.data ? (
        <p className="text-sm text-accent">{t("error.network.unavailable")}</p>
      ) : (
        <>
          <div
            className={`rounded-md border px-5 py-4 ${
              attention.length > 0
                ? "border-accent bg-accent-soft"
                : "border-cue-green bg-cue-green-soft"
            }`}
          >
            <p className="font-display text-lg text-ink">
              {attention.length > 0
                ? t("health.jobsUnhealthy", { count: attention.length })
                : t("health.jobsHealthy", { count: jobs.length })}
            </p>
            <p className="mt-1 text-sm text-ink-soft">{t("health.jobsExplain")}</p>
          </div>

          <section className="mt-10">
            <h2 className="font-display text-lg text-ink">{t("health.jobs")}</h2>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <caption className="sr-only">{t("health.jobs")}</caption>
                <thead>
                  <tr className="border-b border-paper-deep text-left text-ink-muted">
                    <th scope="col" className="px-3 py-2.5 font-medium">{t("health.column.job")}</th>
                    <th scope="col" className="px-3 py-2.5 font-medium">{t("health.column.state")}</th>
                    <th scope="col" className="px-3 py-2.5 font-medium">{t("health.column.lastRun")}</th>
                    <th scope="col" className="px-3 py-2.5 font-medium">{t("health.column.duration")}</th>
                    <th scope="col" className="px-3 py-2.5 font-medium">{t("health.column.expected")}</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((job) => (
                    <tr key={job.name} className="border-b border-paper-warm last:border-0">
                      <td className="px-3 py-3 text-ink">{job.name}</td>
                      <td className={`px-3 py-3 ${JOB_STATE_TONE[job.state]}`}>
                        {t(JOB_STATE_KEY[job.state], { count: job.consecutiveFailures })}
                      </td>
                      <td className="px-3 py-3 text-ink-muted">
                        {job.finishedAt ? formatDateTime(locale, job.finishedAt) : null}
                      </td>
                      <td className="px-3 py-3 text-ink-muted">
                        {job.durationMs > 0
                          ? t("retention.seconds", {
                              seconds: Math.round(job.durationMs / 100) / 10,
                            })
                          : null}
                      </td>
                      <td className="px-3 py-3 text-ink-muted">
                        {job.expectedEveryHours
                          ? t("health.everyHours", { hours: job.expectedEveryHours })
                          : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="mt-12">
            <h2 className="font-display text-lg text-ink">{t("health.sessions")}</h2>
            <p className="mt-2 text-sm text-ink-soft">
              {t("health.sessionsNow", {
                active: health.data.sessions.active,
                online: health.data.sessions.online,
                offline: health.data.sessions.offline,
              })}
            </p>
            {/* A count, never a list: which classes are mid-lesson is theirs. */}
            <p className="mt-1 text-sm text-ink-muted">{t("health.sessionsPrivacy")}</p>
          </section>

          <section className="mt-12">
            <h2 className="font-display text-lg text-ink">{t("health.counts")}</h2>
            <p className="mt-1 max-w-3xl text-sm text-ink-muted">{t("health.countsExplain")}</p>

            <div className="mt-4 grid gap-6 sm:grid-cols-2">
              {METRICS.map((metric) => {
                const points = series(health.data!.counts, metric, 14, health.data!.at);
                const today = points.at(-1)?.value ?? 0;
                const spiking = todayIsSpiking(health.data!.counts, metric, health.data!.at);

                return (
                  <div
                    key={metric}
                    className="rounded-md border border-paper-deep bg-white px-5 py-4"
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <h3 className="text-sm text-ink-muted">{t(`health.metric.${metric}`)}</h3>
                      <p
                        className={`font-display text-2xl ${spiking ? "text-accent" : "text-ink"}`}
                      >
                        {formatNumber(locale, today)}
                      </p>
                    </div>

                    {spiking ? (
                      <p className="mt-1 text-sm text-accent">{t("health.spike")}</p>
                    ) : null}

                    <p className="mt-2 text-sm text-ink-muted">
                      {t("health.fortnight", {
                        total: points.reduce((sum, point) => sum + point.value, 0),
                      })}
                    </p>
                  </div>
                );
              })}
            </div>
          </section>
        </>
      )}
    </SectionShell>
  );
}
